import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import hotkeys from 'hotkeys-js';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as editCustomerActionCreators from '@makemydeal/dr-dash-store/actions/editCustomerActionCreators';
import * as editCustomerSelectors from '@makemydeal/dr-dash-store/selectors/editCustomerSelectors';
import * as customerUIActionCreators from '@makemydeal/dr-dash-store/actions/customerUIActionCreators';
import * as customerUISelectors from '@makemydeal/dr-dash-store/selectors/customerUISelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import { CO_BUYER } from '@makemydeal/dr-shared-types';
import type { CommonConsumer } from '@makemydeal/dr-platform-types';
import {
    initializeHotkeys,
    isBlockingModalOpen,
    closeOpenSnackbars,
    createHotkeyWithCapsLock,
    useCustomerUI
} from '@makemydeal/dr-dash-components';

/**
 * Hook to register Co-Buyer slideout hotkey (Alt+C+I) for compact UI mode.
 *
 * Behavior:
 * - If Co-Buyer exists: Opens/toggles the Co-Buyer edit slideout
 * - If no Co-Buyer: Opens/toggles the customer search to add a Co-Buyer
 *
 * Supports toggle behavior - pressing again closes the slideout/search.
 */
export const useCoBuyerHotkey = (enabled = true): void => {
    const dispatch = useDispatch();
    const currentCustomerSlideout = useSelector(editCustomerSelectors.showEditCustomerSlideout);
    const customerSearchShowing = useSelector(customerUISelectors.getCustomerSearchShowing);
    const coBuyerExists = useSelector(offerReduxSelectors.hasCoBuyer);
    const { showSearch } = useCustomerUI();

    const setCoBuyerAndCloseSearch = useCallback(
        (customer: CommonConsumer) => {
            dispatch(offerReduxActionCreators.updateCoBuyer(customer));
            showSearch(false);
        },
        [dispatch, showSearch]
    );

    // Use refs to always have the latest values in the hotkey handler
    const currentCustomerSlideoutRef = useRef(currentCustomerSlideout);
    const customerSearchShowingRef = useRef(customerSearchShowing);
    const coBuyerExistsRef = useRef(coBuyerExists);
    const showSearchRef = useRef(showSearch);
    const setCoBuyerAndCloseSearchRef = useRef(setCoBuyerAndCloseSearch);

    useEffect(() => {
        currentCustomerSlideoutRef.current = currentCustomerSlideout;
    }, [currentCustomerSlideout]);

    useEffect(() => {
        customerSearchShowingRef.current = customerSearchShowing;
    }, [customerSearchShowing]);

    useEffect(() => {
        coBuyerExistsRef.current = coBuyerExists;
    }, [coBuyerExists]);

    useEffect(() => {
        showSearchRef.current = showSearch;
        setCoBuyerAndCloseSearchRef.current = setCoBuyerAndCloseSearch;
    });

    // Lazily initialize hotkey string
    const CO_BUYER_HOTKEY = useMemo(() => createHotkeyWithCapsLock('c+i'), []);

    useEffect(() => {
        if (!enabled) {
            return undefined;
        }

        initializeHotkeys();

        hotkeys(CO_BUYER_HOTKEY, function (event): void {
            event.preventDefault();

            if (isBlockingModalOpen()) {
                return;
            }

            closeOpenSnackbars();

            if (coBuyerExistsRef.current) {
                // Co-Buyer exists - toggle the Co-Buyer slideout
                if (currentCustomerSlideoutRef.current === CO_BUYER) {
                    dispatch(editCustomerActionCreators.hideEditCustomerSlideout());
                } else {
                    // Close all other panels first before opening Co-Buyer slideout
                    dispatch(baseDealActionCreators.closeSlideOut());
                    dispatch(editCustomerActionCreators.hideEditCustomerSlideout()); // Close Buyer if open
                    dispatch(customerUIActionCreators.showCustomerSearch(false));
                    dispatch(editCustomerActionCreators.showEditCoBuyerSlideout());
                }
            } else {
                // No Co-Buyer - toggle the search
                if (customerSearchShowingRef.current) {
                    dispatch(customerUIActionCreators.showCustomerSearch(false));
                } else {
                    // Close all other panels first before showing search
                    dispatch(baseDealActionCreators.closeSlideOut());
                    dispatch(editCustomerActionCreators.hideEditCustomerSlideout());
                    showSearchRef.current(true, setCoBuyerAndCloseSearchRef.current);
                }
            }
        });

        return () => {
            hotkeys.unbind(CO_BUYER_HOTKEY);
        };
    }, [dispatch, enabled, CO_BUYER_HOTKEY]);
};
